// Hasta çalışma alanı sekmelerinin (Tedaviler, Anamnez, Tetkik) ortak düz kabı:
// araç satırı + bildirim + liste | editör. Kutusuz, ayrım çizgilerle.
// Kurallar: docs/design-principles.md §8.3

import React from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  CloseRounded as CloseIcon,
  LockOutlined as LockIcon,
} from "@mui/icons-material";

import EmptyState from "../../atoms/EmptyState";
import { COLORS, TYPO } from "../../../styles/uiConstants";

// Başlık şeridi hizası (32px) — liste satırlarında 3px seçim şeridi dahil
export const LIST_ROW_SX = { pl: { xs: 1.625, md: 3.625 }, pr: 2, py: 1.5 };

export const WORKSPACE_ROOT_SX = {
  display: "flex",
  flexDirection: "column",
  height: { xs: "auto", md: "100%" },
  minHeight: { xs: 540, md: 480 },
};

const BODY_SX = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  bgcolor: COLORS.white,
};

const SPLIT_SX = {
  flex: 1,
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "340px 1fr" },
};

const LIST_COL_SX = {
  borderRight: { xs: "none", md: `1px solid ${COLORS.slate200}` },
  borderBottom: { xs: `1px solid ${COLORS.slate200}`, md: "none" },
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
};

/**
 * Sekme gövdesi: araç satırı ("Başlık · sayı" + aksiyon) ve sekme içi bildirim.
 *
 * @param {string} title
 * @param {number} [count]
 * @param {React.ReactNode} [action]
 * @param {{open: boolean, severity: string, message: string}} [alert]
 * @param {() => void} [onAlertClose]
 */
export function WorkspaceBody({ title, count, action, alert, onAlertClose, children }) {
  return (
    <Box sx={BODY_SX}>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          minHeight: 56,
          px: { xs: 2, md: 4 },
          py: 1,
          borderBottom: `1px solid ${COLORS.slate200}`,
        }}
      >
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate700 }}>
          {title}
          {count ? (
            <Box component="span" sx={{ color: COLORS.slate400, fontWeight: 500 }}>
              {` · ${count}`}
            </Box>
          ) : null}
        </Typography>
        {action}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {alert ? (
          <Collapse in={alert.open} unmountOnExit>
            <Alert
              severity={alert.severity}
              onClose={onAlertClose}
              variant="filled"
              sx={{
                // Üst ve alt eşit boşluk; yatayda başlık şeridi hizası
                mx: { xs: 2, md: 4 },
                my: 1.5,
                fontWeight: 600,
                fontSize: "0.875rem",
                color: COLORS.white,
                "& .MuiAlert-icon": { color: COLORS.white },
                "& .MuiAlert-action svg": { color: COLORS.white },
              }}
            >
              {alert.message}
            </Alert>
          </Collapse>
        ) : null}
        {children}
      </Box>
    </Box>
  );
}

/** Liste | editör ızgarası. Sol sütun kendi içinde kayar. */
export function WorkspaceSplit({ list, editor }) {
  return (
    <Box sx={SPLIT_SX}>
      <Box sx={LIST_COL_SX}>{list}</Box>
      <Box sx={{ minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {editor}
      </Box>
    </Box>
  );
}

/** İlk yükleme iskeleti: liste satırları | editör alanları, kutusuz. */
export function WorkspaceSkeleton() {
  return (
    <Box sx={SPLIT_SX}>
      <Box sx={{ ...LIST_COL_SX, overflow: "hidden" }}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{ ...LIST_ROW_SX, borderBottom: `1px solid ${COLORS.slate100}` }}
          >
            <Skeleton variant="text" width={`${50 + (i % 3) * 15}%`} height={20} />
            <Skeleton variant="text" width={`${35 + (i % 2) * 15}%`} height={16} />
          </Box>
        ))}
      </Box>
      <Box sx={{ pl: { xs: 2, md: 3 }, pr: { xs: 2, md: 4 }, pt: 2 }}>
        {[0, 1, 2, 3, 4, 5].map((k) => (
          <Box key={k} sx={{ display: "flex", gap: 2, alignItems: "center", py: 1.25 }}>
            <Skeleton variant="text" width={110} height={18} />
            <Skeleton variant="text" width={`${40 + (k % 3) * 18}%`} height={20} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Listenin başındaki "Yeni … oluşturuluyor" satırı; sağdaki çarpı ile
 * kaydetmeden akıştan çıkılır.
 */
export function CreatingRow({ label, hint, onCancel, palette }) {
  const tone = palette?.primary || COLORS.slate700;
  return (
    <Box
      sx={{
        ...LIST_ROW_SX,
        pr: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderLeft: `3px dashed ${tone}`,
        bgcolor: alpha(tone, 0.06),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: tone }}>{label}</Typography>
        {hint ? (
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mt: 0.25 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
      {onCancel ? (
        <Tooltip title="Vazgeç">
          <IconButton
            size="small"
            aria-label="Vazgeç"
            onClick={onCancel}
            sx={{
              flexShrink: 0,
              color: COLORS.slate500,
              "&:hover": { color: COLORS.errorDark, bgcolor: alpha(COLORS.error, 0.08) },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

/**
 * Hiç kayıt yokken liste | editör yerine tüm alanı kaplayan tek boş durum;
 * açıklamada satır içi "oluşturun" bağlantısı.
 *
 * @param {string} lead - Bağlantıdan önceki metin ("Hastanın ilk tedavi planını")
 * @param {string} [tail] - Bağlantıdan sonraki metin
 * @param {string} readOnlyText - Oluşturma yetkisi yokken açıklama
 * @param {string} [linkLabel="oluşturun"] - Satır içi bağlantının metni
 */
export function WorkspaceEmpty({
  icon,
  title,
  lead,
  tail,
  readOnlyText,
  onCreate,
  palette,
  linkLabel = "oluşturun",
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pb: 6,
      }}
    >
      <EmptyState
        variant="illustrated"
        icon={icon}
        title={title}
        description={
          onCreate ? (
            <>
              {lead}{" "}
              <ButtonBase
                onClick={onCreate}
                sx={{
                  font: "inherit",
                  color: palette?.primary,
                  fontWeight: 600,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  verticalAlign: "baseline",
                }}
              >
                {linkLabel}
              </ButtonBase>
              .{tail ? ` ${tail}` : ""}
            </>
          ) : (
            readOnlyText
          )
        }
      />
    </Box>
  );
}

/** Kayıt var ama seçili değilken editör alanı. */
export function SelectPrompt({ icon, title, description }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 320,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: 6,
      }}
    >
      <EmptyState variant="illustrated-sm" icon={icon} title={title} description={description} />
    </Box>
  );
}

/** Liste içi grup başlığı ("Reçete Talebi · 2", "Tamamlanan Diyetler"). */
export function ListGroupLabel({ children, count, first = false }) {
  return (
    <Typography
      sx={{
        pl: { xs: 2, md: 4 },
        pr: 2,
        pt: first ? 1.5 : 2,
        pb: 0.75,
        fontSize: TYPO.caption,
        fontWeight: 600,
        color: COLORS.slate500,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderTop: first ? "none" : `1px solid ${COLORS.slate100}`,
      }}
    >
      {children}
      {count ? (
        <Box component="span" sx={{ color: COLORS.slate400, fontWeight: 500 }}>
          {` · ${count}`}
        </Box>
      ) : null}
    </Typography>
  );
}

/** Editörün üstünde ince "salt okunur" şeridi (tamamlanmış kayıt vb.). */
export function ReadOnlyStrip({ children }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: { xs: 2, md: 3 },
        pr: { xs: 2, md: 4 },
        py: 1,
        bgcolor: COLORS.slate50,
        borderBottom: `1px solid ${COLORS.slate100}`,
      }}
    >
      <LockIcon sx={{ fontSize: 16, color: COLORS.slate500 }} />
      <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate600 }}>
        {children}
      </Typography>
    </Box>
  );
}
